import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Autocomplete,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Checkbox,
  ListItemText,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';
import {
  fetchSupervisorBanks,
  fetchAllBillingRecords,
  submitBillingRecords,
  bulkUpdateBillingRecords,
  updateBillingStatus,
} from '../actions/billingMasterActions';
import {
  BILLING_SUBMIT_RESET,
  BILLING_BULK_UPDATE_RESET,
  BILLING_STATUS_UPDATE_RESET,
} from '../constants/billingMasterConstants';
import {
  fetchAllProvisionalBillingRecords,
  submitProvisionalBillingRecords,
  bulkUpdateProvisionalBillingRecords,
} from '../actions/provisionalBillingActions';
import {
  PROVISIONAL_BILLING_SUBMIT_RESET,
  PROVISIONAL_BILLING_BULK_UPDATE_RESET,
} from '../constants/provisionalBillingConstants';
import { listCollectionSupervisors } from '../actions/supervisorMasterActions';
import {
  fetchAllInvoiceRecords,
  createInvoiceRecord,
  updateInvoiceRecord,
  deleteInvoiceRecord,
} from '../actions/invoiceDataActions';
import {
  INVOICE_DATA_CREATE_RESET,
  INVOICE_DATA_UPDATE_RESET,
  INVOICE_DATA_DELETE_RESET,
} from '../constants/invoiceDataConstants';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);
const BILL_STATUS_OPTIONS = ['Raised', 'Not Raised'];
const PAYMENT_STATUS_OPTIONS = ['Pending from Bank', 'Paid'];
const SELECT_ALL_ID = '__ALL__';
const SELECT_ALL_OPTION = { _id: SELECT_ALL_ID, name: 'Select All' };
const DEPARTMENT_OPTIONS = [
  { value: 'collection', label: 'Collection' },
  { value: 'verification', label: 'Verification' },
];

const sectionKey = (supervisorId, bank, product) => `${supervisorId}__${bank}__${product}`;

const isDelhiRegion = (region) => (region || '').trim().toLowerCase() === 'delhi';

const emptyInvoiceForm = (department, company, month, year, gstNumber, cgstSgst, igst) => ({
  company: company || '',
  month,
  year,
  invoiceDate: '',
  billMonth: month,
  invoiceNumber: '',
  department: department || 'collection',
  balanceDue: '',
  refNo: '',
  gstNumber: gstNumber || '',
  cgstSgst: cgstSgst ?? '',
  igst: igst ?? '',
  paymentReceived: '',
  tdsAmount: '',
  totalAmount: '',
  billTAT: '',
  paymentTAT: '',
  totalTAT: '',
});

const emptySection = (bank, product) => ({
  bank,
  product,
  _id: null,
  provisionalBilling: '',
  billingRemarks: '',
  billStatus: '',
  billNotRaised: '',
  totalBillRaised: '',
  tds: '',
  billAmountAfterTDS: '',
  paymentStatus: 'Pending from Bank',
});

const calcAfterTDS = (total, tds) => {
  const t = parseFloat(total);
  const d = parseFloat(tds);
  if (!isNaN(t) && !isNaN(d)) return (t - (t * d) / 100).toFixed(2);
  return '';
};

const calcTotalAmount = (paymentReceived, tdsAmount) => {
  const p = parseFloat(paymentReceived);
  const t = parseFloat(tdsAmount);
  if (!isNaN(p) && !isNaN(t)) return (p - t).toFixed(2);
  return '';
};

const calcTotalTAT = (billTAT, paymentTAT) => {
  const b = parseFloat(billTAT);
  const p = parseFloat(paymentTAT);
  if (!isNaN(b) && !isNaN(p)) return b + p;
  return '';
};

const BillingMaster = () => {
  const dispatch = useDispatch();

  const [month, setMonth] = useState(CURRENT_MONTH);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [groups, setGroups] = useState([]);
  const [selectedSupervisorIds, setSelectedSupervisorIds] = useState([]);
  const [sectionTabs, setSectionTabs] = useState({});
  const [invoiceFormOpen, setInvoiceFormOpen] = useState({});
  const [invoiceForms, setInvoiceForms] = useState({});
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [provisionalFillForms, setProvisionalFillForms] = useState({});

  const getRoleData = useSelector((s) => s.getRoleInfo?.getRoleData || {});
  const { banks: supervisorGroups = [], loading: loadingBanks } = useSelector((s) => s.billingSupervisorBanks);
  const { records: allBillingRecords = [], loading: loadingRecords } = useSelector((s) => s.billingAllRecords);
  const billingSubmit = useSelector((s) => s.billingSubmit);
  const billingBulkUpdate = useSelector((s) => s.billingBulkUpdate);
  const billingStatusUpdate = useSelector((s) => s.billingStatusUpdate);
  const { records: allProvisionalRecords = [], loading: loadingProvisional } = useSelector(
    (s) => s.provisionalBillingAllRecords,
  );
  const provisionalBillingSubmit = useSelector((s) => s.provisionalBillingSubmit);
  const provisionalBillingBulkUpdate = useSelector((s) => s.provisionalBillingBulkUpdate);
  const { loading: loadingSupervisors, supervisors = [] } = useSelector((s) => s.supervisorUsers);
  const { records: allInvoiceRecords = [], loading: loadingInvoices } = useSelector((s) => s.invoiceDataAllRecords);
  const invoiceCreate = useSelector((s) => s.invoiceDataCreate);
  const invoiceUpdate = useSelector((s) => s.invoiceDataUpdate);
  const invoiceDelete = useSelector((s) => s.invoiceDataDelete);

  const loadData = useCallback(() => {
    dispatch(fetchSupervisorBanks(month, year));
    dispatch(fetchAllBillingRecords(month, year));
    dispatch(fetchAllProvisionalBillingRecords(month, year));
    dispatch(fetchAllInvoiceRecords());
  }, [dispatch, month, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    dispatch(listCollectionSupervisors());
  }, [dispatch]);

  // Build one group per supervisor, each holding its bank+product sections
  useEffect(() => {
    if (loadingBanks || loadingRecords || loadingProvisional) return;

    const bySupervisor = new Map();
    const getEntry = (supervisorId, supervisorName) => {
      if (!bySupervisor.has(supervisorId)) {
        bySupervisor.set(supervisorId, { supervisorId, supervisorName, combos: [] });
      }
      return bySupervisor.get(supervisorId);
    };

    for (const sg of supervisorGroups) {
      const entry = getEntry(sg.supervisorId, sg.supervisorName);
      for (const b of sg.banks) {
        for (const p of b.products) {
          entry.combos.push({
            bank: b.bankName, product: p.productName, department: p.department, company: p.company,
            region: p.region, gstNumber: p.gstNumber, cgstSgst: p.cgstSgst, igst: p.igst,
          });
        }
      }
    }

    const newGroups = Array.from(bySupervisor.values())
      .filter((entry) => entry.combos.length > 0)
      .map((entry) => {
        const sections = entry.combos.map(({ bank, product, department, company, region, gstNumber, cgstSgst, igst }) => {
          const existing = allBillingRecords.find(
            (r) => r.supervisorName?._id === entry.supervisorId && r.bank === bank && r.product === product,
          );
          const provisional = allProvisionalRecords.find(
            (r) => r.supervisorName?._id === entry.supervisorId && r.bank === bank && r.product === product,
          );
          const base = existing
            ? {
                bank,
                product,
                _id: existing._id,
                provisionalBilling: existing.provisionalBilling ?? '',
                billingRemarks: existing.billingRemarks || '',
                billStatus: existing.billStatus || '',
                billNotRaised: existing.billNotRaised ?? '',
                totalBillRaised: existing.totalBillRaised ?? '',
                tds: existing.tds ?? '',
                billAmountAfterTDS: existing.billAmountAfterTDS ?? '',
                paymentStatus: existing.paymentStatus || 'Pending from Bank',
              }
            : emptySection(bank, product);

          return {
            ...base,
            department,
            company,
            region,
            gstNumber,
            cgstSgst,
            igst,
            provisionalBillingAmount: provisional?.provisionalBilling ?? null,
            provisionalTotalCollection: provisional?.totalCollection ?? null,
            provisionalRecordId: provisional?._id ?? null,
            isEditing: false,
          };
        });

        return {
          supervisorId: entry.supervisorId,
          supervisorName: entry.supervisorName,
          sections,
        };
      });

    newGroups.sort((a, b) => a.supervisorName.localeCompare(b.supervisorName));

    setGroups(newGroups);
  }, [supervisorGroups, allBillingRecords, allProvisionalRecords, loadingBanks, loadingRecords, loadingProvisional]);

  // Submit success
  useEffect(() => {
    if (billingSubmit.success) {
      toast.success('Billing records submitted successfully');
      dispatch({ type: BILLING_SUBMIT_RESET });
      loadData();
    }
    if (billingSubmit.error) {
      toast.error(billingSubmit.error);
      dispatch({ type: BILLING_SUBMIT_RESET });
    }
  }, [billingSubmit.success, billingSubmit.error]);

  // Bulk update success
  useEffect(() => {
    if (billingBulkUpdate.success) {
      toast.success('Billing records updated successfully');
      dispatch({ type: BILLING_BULK_UPDATE_RESET });
      loadData();
    }
    if (billingBulkUpdate.error) {
      toast.error(billingBulkUpdate.error);
      dispatch({ type: BILLING_BULK_UPDATE_RESET });
    }
  }, [billingBulkUpdate.success, billingBulkUpdate.error]);

  // Payment status update success
  useEffect(() => {
    if (billingStatusUpdate.success) {
      toast.success('Payment status updated successfully');
      dispatch({ type: BILLING_STATUS_UPDATE_RESET });
    }
    if (billingStatusUpdate.error) {
      toast.error(billingStatusUpdate.error);
      dispatch({ type: BILLING_STATUS_UPDATE_RESET });
      loadData();
    }
  }, [billingStatusUpdate.success, billingStatusUpdate.error]);

  // Provisional billing fill-on-behalf: submit (create) success
  useEffect(() => {
    if (provisionalBillingSubmit.success) {
      toast.success('Provisional billing filled successfully');
      dispatch({ type: PROVISIONAL_BILLING_SUBMIT_RESET });
      loadData();
    }
    if (provisionalBillingSubmit.error) {
      toast.error(provisionalBillingSubmit.error);
      dispatch({ type: PROVISIONAL_BILLING_SUBMIT_RESET });
    }
  }, [provisionalBillingSubmit.success, provisionalBillingSubmit.error]);

  // Provisional billing fill-on-behalf: update success
  useEffect(() => {
    if (provisionalBillingBulkUpdate.success) {
      toast.success('Provisional billing updated successfully');
      dispatch({ type: PROVISIONAL_BILLING_BULK_UPDATE_RESET });
      loadData();
    }
    if (provisionalBillingBulkUpdate.error) {
      toast.error(provisionalBillingBulkUpdate.error);
      dispatch({ type: PROVISIONAL_BILLING_BULK_UPDATE_RESET });
    }
  }, [provisionalBillingBulkUpdate.success, provisionalBillingBulkUpdate.error]);

  // Invoice create success
  useEffect(() => {
    if (invoiceCreate.success) {
      toast.success('Invoice data added successfully');
      dispatch({ type: INVOICE_DATA_CREATE_RESET });
      dispatch(fetchAllInvoiceRecords());
    }
    if (invoiceCreate.error) {
      toast.error(invoiceCreate.error);
      dispatch({ type: INVOICE_DATA_CREATE_RESET });
    }
  }, [invoiceCreate.success, invoiceCreate.error]);

  // Invoice update success
  useEffect(() => {
    if (invoiceUpdate.success) {
      toast.success('Invoice data updated successfully');
      dispatch({ type: INVOICE_DATA_UPDATE_RESET });
      dispatch(fetchAllInvoiceRecords());
    }
    if (invoiceUpdate.error) {
      toast.error(invoiceUpdate.error);
      dispatch({ type: INVOICE_DATA_UPDATE_RESET });
    }
  }, [invoiceUpdate.success, invoiceUpdate.error]);

  // Invoice delete success
  useEffect(() => {
    if (invoiceDelete.success) {
      toast.success('Invoice data deleted successfully');
      dispatch({ type: INVOICE_DATA_DELETE_RESET });
      dispatch(fetchAllInvoiceRecords());
    }
    if (invoiceDelete.error) {
      toast.error(invoiceDelete.error);
      dispatch({ type: INVOICE_DATA_DELETE_RESET });
    }
  }, [invoiceDelete.success, invoiceDelete.error]);

  const updateSection = (supervisorId, sectionIdx, field, value) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.supervisorId !== supervisorId) return g;
        const sections = g.sections.map((s, si) => {
          if (si !== sectionIdx) return s;
          const next = { ...s, [field]: value };
          if (field === 'totalBillRaised' || field === 'tds') {
            const total = field === 'totalBillRaised' ? value : s.totalBillRaised;
            const tds = field === 'tds' ? value : s.tds;
            next.billAmountAfterTDS = calcAfterTDS(total, tds);
          }
          if (field === 'billStatus' && value === 'Raised') {
            next.billNotRaised = '';
          }
          return next;
        });
        return { ...g, sections };
      }),
    );
  };

  const handlePaymentStatusChange = (supervisorId, sectionIdx, id, value) => {
    updateSection(supervisorId, sectionIdx, 'paymentStatus', value);
    if (id) {
      dispatch(updateBillingStatus(id, value));
    }
  };

  const setSectionEditing = (supervisorId, sectionIdx, editing) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.supervisorId !== supervisorId) return g;
        return {
          ...g,
          sections: g.sections.map((s, si) => (si === sectionIdx ? { ...s, isEditing: editing } : s)),
        };
      }),
    );
  };

  const updateProvisionalFillField = (supervisorId, bank, product, field, value) => {
    const key = sectionKey(supervisorId, bank, product);
    setProvisionalFillForms((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleProvisionalFillSave = (supervisorId, section) => {
    const key = sectionKey(supervisorId, section.bank, section.product);
    const form = provisionalFillForms[key] || {};
    const provisionalBilling = form.provisionalBilling !== undefined ? form.provisionalBilling : '';
    const totalCollection = form.totalCollection !== undefined ? form.totalCollection : '';

    if (section.provisionalRecordId) {
      dispatch(bulkUpdateProvisionalBillingRecords([{
        _id: section.provisionalRecordId,
        provisionalBilling,
        totalCollection,
        members: [],
      }]));
    } else {
      dispatch(submitProvisionalBillingRecords({
        month,
        year,
        supervisorName: supervisorId,
        sections: [{ bank: section.bank, product: section.product, provisionalBilling, totalCollection }],
      }));
    }
  };

  const handleSubmit = (supervisorId, sectionIdx) => {
    const group = groups.find((g) => g.supervisorId === supervisorId);
    if (!group) return;
    const section = group.sections[sectionIdx];
    if (!section.billStatus) return toast.error(`Bill Status is required for ${section.bank} - ${section.product}`);

    dispatch(submitBillingRecords({
      month,
      year,
      sections: [{
        supervisorName: supervisorId,
        bank: section.bank,
        product: section.product,
        provisionalBilling: section.provisionalBilling,
        billingRemarks: section.billingRemarks,
        billStatus: section.billStatus,
        billNotRaised: section.billNotRaised,
        totalBillRaised: section.totalBillRaised,
        tds: section.tds,
        billAmountAfterTDS: section.billAmountAfterTDS,
        paymentStatus: section.paymentStatus,
      }],
    }));
  };

  const handleUpdate = (supervisorId, sectionIdx) => {
    const group = groups.find((g) => g.supervisorId === supervisorId);
    if (!group) return;
    const section = group.sections[sectionIdx];
    if (!section.billStatus) return toast.error(`Bill Status is required for ${section.bank} - ${section.product}`);

    dispatch(bulkUpdateBillingRecords([{
      _id: section._id,
      provisionalBilling: section.provisionalBilling,
      billingRemarks: section.billingRemarks,
      billStatus: section.billStatus,
      billNotRaised: section.billNotRaised,
      totalBillRaised: section.totalBillRaised,
      tds: section.tds,
      billAmountAfterTDS: section.billAmountAfterTDS,
    }]));
  };

  const getSectionInvoices = (supervisorId, bank, product) =>
    allInvoiceRecords.filter(
      (r) => r.supervisorName?._id === supervisorId && r.bank === bank && r.product === product,
    );

  const handleTabChange = (supervisorId, bank, product, value) => {
    setSectionTabs((prev) => ({ ...prev, [sectionKey(supervisorId, bank, product)]: value }));
  };

  const openInvoiceForm = (supervisorId, bank, product, department, company, gstNumber, cgstSgst, igst) => {
    const key = sectionKey(supervisorId, bank, product);
    setEditingInvoiceId(null);
    setInvoiceForms((prev) => ({
      ...prev,
      [key]: emptyInvoiceForm(department, company, month, year, gstNumber, cgstSgst, igst),
    }));
    setInvoiceFormOpen((prev) => ({ ...prev, [key]: true }));
  };

  const openInvoiceEditForm = (supervisorId, bank, product, record) => {
    const key = sectionKey(supervisorId, bank, product);
    const group = groups.find((g) => g.supervisorId === supervisorId);
    const section = group?.sections.find((s) => s.bank === bank && s.product === product);
    setEditingInvoiceId(record._id);
    setInvoiceForms((prev) => ({
      ...prev,
      [key]: {
        company: record.company || '',
        month: record.month,
        year: record.year,
        invoiceDate: record.invoiceDate ? record.invoiceDate.slice(0, 10) : '',
        billMonth: record.billMonth,
        invoiceNumber: record.invoiceNumber || '',
        department: record.department || 'collection',
        balanceDue: record.balanceDue ?? '',
        refNo: record.refNo || '',
        // GST fields are read-only and derived, so always pull the current Supervisor Master
        // values on edit rather than the frozen value the record was originally saved with.
        gstNumber: section?.gstNumber || '',
        cgstSgst: section?.cgstSgst ?? '',
        igst: section?.igst ?? '',
        paymentReceived: record.paymentReceived ?? '',
        tdsAmount: record.tdsAmount ?? '',
        totalAmount: record.totalAmount ?? '',
        billTAT: record.billTAT ?? '',
        paymentTAT: record.paymentTAT ?? '',
        totalTAT: record.totalTAT ?? '',
      },
    }));
    setInvoiceFormOpen((prev) => ({ ...prev, [key]: true }));
  };

  const closeInvoiceForm = (supervisorId, bank, product) => {
    const key = sectionKey(supervisorId, bank, product);
    setInvoiceFormOpen((prev) => ({ ...prev, [key]: false }));
    setEditingInvoiceId(null);
  };

  const updateInvoiceFormField = (supervisorId, bank, product, field, value) => {
    const key = sectionKey(supervisorId, bank, product);
    setInvoiceForms((prev) => {
      const form = { ...prev[key], [field]: value };
      if (field === 'paymentReceived' || field === 'tdsAmount') {
        const paymentReceived = field === 'paymentReceived' ? value : form.paymentReceived;
        const tdsAmount = field === 'tdsAmount' ? value : form.tdsAmount;
        form.totalAmount = calcTotalAmount(paymentReceived, tdsAmount);
      }
      if (field === 'billTAT' || field === 'paymentTAT') {
        const billTAT = field === 'billTAT' ? value : form.billTAT;
        const paymentTAT = field === 'paymentTAT' ? value : form.paymentTAT;
        form.totalTAT = calcTotalTAT(billTAT, paymentTAT);
      }
      return { ...prev, [key]: form };
    });
  };

  const handleInvoiceSave = (supervisorId, bank, product) => {
    const key = sectionKey(supervisorId, bank, product);
    const form = invoiceForms[key];
    if (!form) return;
    if (!form.company) return toast.error('Company is required');
    if (!form.invoiceDate) return toast.error('Invoice date is required');
    if (!form.invoiceNumber) return toast.error('Invoice number is required');

    const group = groups.find((g) => g.supervisorId === supervisorId);
    const section = group?.sections.find((s) => s.bank === bank && s.product === product);
    const isDelhi = isDelhiRegion(section?.region);
    const isPaid = section?.paymentStatus === 'Paid';

    const payload = {
      supervisorName: supervisorId,
      bank,
      product,
      company: form.company,
      month: form.month,
      year: form.year,
      invoiceDate: form.invoiceDate,
      billMonth: form.billMonth,
      invoiceNumber: form.invoiceNumber,
      department: form.department,
      balanceDue: form.balanceDue,
      refNo: form.refNo,
      gstNumber: form.gstNumber,
      cgstSgst: isDelhi ? form.cgstSgst : null,
      igst: isDelhi ? null : form.igst,
      paymentReceived: isPaid ? form.paymentReceived : null,
      tdsAmount: isPaid ? form.tdsAmount : null,
      totalAmount: isPaid ? form.totalAmount : null,
      billTAT: isPaid ? form.billTAT : null,
      paymentTAT: isPaid ? form.paymentTAT : null,
      totalTAT: isPaid ? form.totalTAT : null,
    };

    if (editingInvoiceId) {
      dispatch(updateInvoiceRecord(editingInvoiceId, payload));
    } else {
      dispatch(createInvoiceRecord(payload));
    }
    closeInvoiceForm(supervisorId, bank, product);
  };

  const handleInvoiceDelete = (id) => {
    dispatch(deleteInvoiceRecord(id));
  };

  const isSaving = billingSubmit.loading || billingBulkUpdate.loading;
  const isInvoiceSaving = invoiceCreate.loading || invoiceUpdate.loading;
  const canAddInvoice = getRoleData.billingMasterAdd === true;
  const canUpdateInvoice = getRoleData.billingMasterUpdate === true;
  const canDeleteInvoice = getRoleData.billingMasterDelete === true;
  const isLoading = loadingBanks || loadingRecords || loadingProvisional;
  const canUpdateStatus = getRoleData.billingMasterStatusUpdate === true;
  const canFillProvisionalOnBehalf = getRoleData.provisionalBillingFillOnBehalf === true;
  const isProvisionalFillSaving = provisionalBillingSubmit.loading || provisionalBillingBulkUpdate.loading;

  const selectedSupervisors = supervisors.filter((s) => selectedSupervisorIds.includes(s._id));
  const visibleGroups = selectedSupervisorIds.length === 0
    ? groups
    : groups.filter((g) => selectedSupervisorIds.includes(g.supervisorId));

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Billing
        </Typography>

        {/* Supervisor search (multi-select) */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              limitTags={2}
              loading={loadingSupervisors}
              options={[SELECT_ALL_OPTION, ...supervisors]}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={selectedSupervisors}
              onChange={(_, newValue) => {
                const clickedSelectAll = newValue.some((v) => v._id === SELECT_ALL_ID);
                if (clickedSelectAll) {
                  const allSelected = supervisors.length > 0 && selectedSupervisorIds.length === supervisors.length;
                  setSelectedSupervisorIds(allSelected ? [] : supervisors.map((s) => s._id));
                  return;
                }
                setSelectedSupervisorIds(newValue.map((v) => v._id));
              }}
              renderOption={(props, option) => {
                const isSelectAll = option._id === SELECT_ALL_ID;
                const allSelected = supervisors.length > 0 && selectedSupervisorIds.length === supervisors.length;
                const checked = isSelectAll ? allSelected : selectedSupervisorIds.includes(option._id);
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={isSelectAll && !allSelected && selectedSupervisorIds.length > 0}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText primary={option.name} />
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Supervisor" placeholder="Search supervisor..." />
              )}
            />
          </Grid>
        </Grid>

        {/* Month / Year selectors */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {MONTH_NAMES.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : visibleGroups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {groups.length === 0
              ? `No bank/product assignments found for ${MONTH_NAMES[month - 1]} ${year}. Please ensure Supervisor Master has entries for this month.`
              : 'No matching supervisor found for the selected filter.'}
          </Typography>
        </Paper>
      ) : (
        visibleGroups.map((group) => {
          const missingProvisionalSections = group.sections.filter((s) => s.provisionalBillingAmount == null);
          const hasMissingProvisional = missingProvisionalSections.length > 0;
          return (
            <Paper key={group.supervisorId} sx={{ p: 3, mb: 3 }}>
              {/* Sub-heading: Supervisor · Month Year */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight="bold" color={hasMissingProvisional ? 'error' : 'primary'}>
                  {group.supervisorName} &nbsp;·&nbsp; {MONTH_NAMES[month - 1]} {year}
                </Typography>
                {/* Tooltip + its child stay permanently mounted and toggle via CSS
                    instead of conditional rendering - unmounting a MUI Tooltip
                    while its Popper is mid-interaction (e.g. hovered right as
                    hasMissingProvisional flips false on a data refresh) throws
                    "Cannot set properties of undefined (setting 'toggle')". */}
                <Tooltip
                  title={
                    hasMissingProvisional
                      ? `Provisional billing not filled for: ${missingProvisionalSections
                          .map((s) => `${s.bank} - ${s.product}`)
                          .join(', ')}`
                      : ''
                  }
                >
                  <Box component="span" sx={{ display: hasMissingProvisional ? 'inline-flex' : 'none' }}>
                    <Chip
                      icon={<WarningAmberIcon fontSize="small" />}
                      label="Provisional Billing Missing"
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Tooltip>
              </Box>

              {/* One section per bank+product */}
              {group.sections.map((section, sectionIdx) => {
                const isSubmitted = section._id != null;
                const isReadOnly = isSubmitted && !section.isEditing;
                const key = sectionKey(group.supervisorId, section.bank, section.product);
                const activeTab = sectionTabs[key] || 'billing';
                const sectionInvoices = getSectionInvoices(group.supervisorId, section.bank, section.product);
                const showInvoiceForm = !!invoiceFormOpen[key];
                const invoiceForm = invoiceForms[key];
                const canFillThisProvisional = canFillProvisionalOnBehalf && section.provisionalBillingAmount == null;
                const provisionalFillForm = provisionalFillForms[key] || {};
                return (
                <Box key={`${section.bank}-${section.product}`} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ px: 1, whiteSpace: 'nowrap', color: 'text.secondary' }}
                    >
                      {section.bank} &nbsp;—&nbsp; {section.product}
                    </Typography>
                    {isSubmitted && (
                      <Chip
                        label={section.isEditing ? 'Editing' : 'Submitted'}
                        color={section.isEditing ? 'warning' : 'success'}
                        size="small"
                      />
                    )}
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  <Tabs
                    value={activeTab}
                    onChange={(_, v) => handleTabChange(group.supervisorId, section.bank, section.product, v)}
                    sx={{ minHeight: 36, mb: 2 }}
                  >
                    <Tab label="Billing" value="billing" sx={{ minHeight: 36, py: 0.5 }} />
                    <Tab label="Invoice Data" value="invoice" sx={{ minHeight: 36, py: 0.5 }} />
                  </Tabs>

                  {activeTab === 'billing' && (
                  <>
                  <Grid container spacing={2}>
                    {/* Provisional Billing details (from the Provisional Billing page) */}
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth size="small"
                        label="Provisional Billing Amount"
                        type={canFillThisProvisional ? 'number' : 'text'}
                        value={
                          canFillThisProvisional
                            ? (provisionalFillForm.provisionalBilling ?? '')
                            : (section.provisionalBillingAmount ?? '')
                        }
                        InputProps={{ readOnly: !canFillThisProvisional }}
                        onChange={canFillThisProvisional
                          ? (e) => updateProvisionalFillField(group.supervisorId, section.bank, section.product, 'provisionalBilling', e.target.value)
                          : undefined}
                        helperText={canFillThisProvisional ? 'Supervisor has not filled this in — you can fill it' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth size="small"
                        label="Total Collection Amount"
                        type={canFillThisProvisional ? 'number' : 'text'}
                        value={
                          canFillThisProvisional
                            ? (provisionalFillForm.totalCollection ?? '')
                            : (section.provisionalTotalCollection ?? '')
                        }
                        InputProps={{ readOnly: !canFillThisProvisional }}
                        onChange={canFillThisProvisional
                          ? (e) => updateProvisionalFillField(group.supervisorId, section.bank, section.product, 'totalCollection', e.target.value)
                          : undefined}
                        helperText={canFillThisProvisional ? 'Supervisor has not filled this in — you can fill it' : ''}
                      />
                    </Grid>
                    {canFillThisProvisional && (
                      <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleProvisionalFillSave(group.supervisorId, section)}
                          disabled={isProvisionalFillSaving}
                          startIcon={isProvisionalFillSaving ? <CircularProgress size={16} /> : null}
                        >
                          {isProvisionalFillSaving ? 'Saving...' : 'Save Provisional Billing'}
                        </Button>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small" disabled={!canUpdateStatus || isReadOnly}>
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                          value={section.paymentStatus || 'Pending from Bank'}
                          label="Payment Status"
                          onChange={(e) => handlePaymentStatusChange(group.supervisorId, sectionIdx, section._id, e.target.value)}
                        >
                          {PAYMENT_STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 1 */}
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Billing Remarks"
                        value={section.billingRemarks}
                        onChange={(e) => updateSection(group.supervisorId, sectionIdx, 'billingRemarks', e.target.value)}
                        InputProps={{ readOnly: isReadOnly }}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormControl fullWidth size="small" required disabled={isReadOnly}>
                        <InputLabel>Bill Status *</InputLabel>
                        <Select
                          value={section.billStatus}
                          label="Bill Status *"
                          onChange={(e) => updateSection(group.supervisorId, sectionIdx, 'billStatus', e.target.value)}
                        >
                          <MenuItem value=""><em>Select</em></MenuItem>
                          {BILL_STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 2 */}
                    {section.billStatus !== 'Raised' && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth size="small"
                          label="Bill Not Raised"
                          type="number"
                          value={section.billNotRaised}
                          onChange={(e) => updateSection(group.supervisorId, sectionIdx, 'billNotRaised', e.target.value)}
                          InputProps={{ readOnly: isReadOnly }}
                          disabled={isReadOnly}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth size="small"
                        label="Total Bill Raised"
                        type="number"
                        value={section.totalBillRaised}
                        onChange={(e) => updateSection(group.supervisorId, sectionIdx, 'totalBillRaised', e.target.value)}
                        InputProps={{ readOnly: isReadOnly }}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        fullWidth size="small"
                        label="TDS (%)"
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        value={section.tds}
                        onChange={(e) => updateSection(group.supervisorId, sectionIdx, 'tds', e.target.value)}
                        InputProps={{ readOnly: isReadOnly }}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        fullWidth size="small"
                        label="Bill Amt After TDS"
                        value={section.billAmountAfterTDS}
                        InputProps={{ readOnly: true }}
                        helperText="Auto-calculated"
                      />
                    </Grid>
                  </Grid>
                  {/* Per-section action button */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    {!isSubmitted && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSubmit(group.supervisorId, sectionIdx)}
                        disabled={isSaving}
                        startIcon={isSaving ? <CircularProgress size={16} /> : null}
                      >
                        {isSaving ? 'Submitting...' : 'Submit'}
                      </Button>
                    )}
                    {isSubmitted && !section.isEditing && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSectionEditing(group.supervisorId, sectionIdx, true)}
                      >
                        Edit
                      </Button>
                    )}
                    {isSubmitted && section.isEditing && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => { setSectionEditing(group.supervisorId, sectionIdx, false); loadData(); }}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleUpdate(group.supervisorId, sectionIdx)}
                          disabled={isSaving}
                          startIcon={isSaving ? <CircularProgress size={16} /> : null}
                        >
                          {isSaving ? 'Updating...' : 'Update'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  </>
                  )}

                  {activeTab === 'invoice' && (
                  <Box>
                    <TableContainer sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell>Month</TableCell>
                            <TableCell>Invoice Date</TableCell>
                            <TableCell>Bill Month</TableCell>
                            <TableCell>Invoice No.</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Balance Due</TableCell>
                            <TableCell>Ref No.</TableCell>
                            <TableCell>GST Number</TableCell>
                            <TableCell>{isDelhiRegion(section.region) ? 'CGST/SGST' : 'IGST'}</TableCell>
                            {section.paymentStatus === 'Paid' && (
                              <>
                                <TableCell>Payment Received</TableCell>
                                <TableCell>TDS Amount</TableCell>
                                <TableCell>Total Amount</TableCell>
                                <TableCell>Bill TAT</TableCell>
                                <TableCell>Payment TAT</TableCell>
                                <TableCell>Total TAT</TableCell>
                              </>
                            )}
                            {(canUpdateInvoice || canDeleteInvoice) && <TableCell align="right">Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingInvoices ? (
                            <TableRow>
                              <TableCell colSpan={section.paymentStatus === 'Paid' ? 17 : 11} align="center" sx={{ py: 2 }}>
                                <CircularProgress size={20} />
                              </TableCell>
                            </TableRow>
                          ) : sectionInvoices.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={section.paymentStatus === 'Paid' ? 17 : 11}>
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                  No invoices added yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            sectionInvoices.map((inv) => (
                              <TableRow key={inv._id}>
                                <TableCell>{inv.company || '-'}</TableCell>
                                <TableCell>{MONTH_NAMES[(inv.month || 1) - 1]}</TableCell>
                                <TableCell>{inv.invoiceDate ? inv.invoiceDate.slice(0, 10) : '-'}</TableCell>
                                <TableCell>{MONTH_NAMES[(inv.billMonth || 1) - 1]}</TableCell>
                                <TableCell>{inv.invoiceNumber}</TableCell>
                                <TableCell>
                                  {DEPARTMENT_OPTIONS.find((d) => d.value === inv.department)?.label || inv.department}
                                </TableCell>
                                <TableCell>{inv.balanceDue ?? '-'}</TableCell>
                                <TableCell>{inv.refNo || '-'}</TableCell>
                                {/* GST fields always reflect current Supervisor Master data for this bank/product, not the frozen value the invoice was saved with */}
                                <TableCell>{section.gstNumber || '-'}</TableCell>
                                <TableCell>
                                  {isDelhiRegion(section.region)
                                    ? (section.cgstSgst != null ? `${section.cgstSgst}%` : '-')
                                    : (section.igst != null ? `${section.igst}%` : '-')}
                                </TableCell>
                                {section.paymentStatus === 'Paid' && (
                                  <>
                                    <TableCell>{inv.paymentReceived ?? '-'}</TableCell>
                                    <TableCell>{inv.tdsAmount ?? '-'}</TableCell>
                                    <TableCell>{inv.totalAmount ?? '-'}</TableCell>
                                    <TableCell>{inv.billTAT ?? '-'}</TableCell>
                                    <TableCell>{inv.paymentTAT ?? '-'}</TableCell>
                                    <TableCell>{inv.totalTAT ?? '-'}</TableCell>
                                  </>
                                )}
                                {(canUpdateInvoice || canDeleteInvoice) && (
                                  <TableCell align="right">
                                    {canUpdateInvoice && (
                                      <IconButton
                                        size="small"
                                        onClick={() => openInvoiceEditForm(group.supervisorId, section.bank, section.product, inv)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    {canDeleteInvoice && (
                                      <IconButton size="small" onClick={() => handleInvoiceDelete(inv._id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {!showInvoiceForm && canAddInvoice && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openInvoiceForm(group.supervisorId, section.bank, section.product, section.department, section.company, section.gstNumber, section.cgstSgst, section.igst)}
                      >
                        Add Invoice Details
                      </Button>
                    )}

                    {showInvoiceForm && invoiceForm && (
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="Company"
                              value={invoiceForm.company}
                              InputProps={{ readOnly: true }}
                              // helperText="From Supervisor Master"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Month</InputLabel>
                              <Select
                                value={invoiceForm.month}
                                label="Month"
                                onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'month', e.target.value)}
                              >
                                {MONTH_NAMES.map((m, i) => (
                                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="Invoice Date *"
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              value={invoiceForm.invoiceDate}
                              onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'invoiceDate', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Bill Month</InputLabel>
                              <Select
                                value={invoiceForm.billMonth}
                                label="Bill Month"
                                onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'billMonth', e.target.value)}
                              >
                                {MONTH_NAMES.map((m, i) => (
                                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="Invoice Number *"
                              value={invoiceForm.invoiceNumber}
                              onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'invoiceNumber', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="Department"
                              value={DEPARTMENT_OPTIONS.find((d) => d.value === invoiceForm.department)?.label || invoiceForm.department}
                              InputProps={{ readOnly: true }}
                              // helperText="From Supervisor Master"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="Balance Due"
                              type="number"
                              value={invoiceForm.balanceDue}
                              onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'balanceDue', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="Ref No"
                              value={invoiceForm.refNo}
                              onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'refNo', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth size="small"
                              label="GST Number"
                              value={invoiceForm.gstNumber}
                              InputProps={{ readOnly: true }}
                              // helperText="From Supervisor Master"
                            />
                          </Grid>
                          {isDelhiRegion(section.region) ? (
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                fullWidth size="small"
                                label="CGST/SGST %"
                                value={invoiceForm.cgstSgst !== '' ? `${invoiceForm.cgstSgst}%` : ''}
                                InputProps={{ readOnly: true }}
                                // helperText="From Supervisor Master"
                              />
                            </Grid>
                          ) : (
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                fullWidth size="small"
                                label="IGST %"
                                value={invoiceForm.igst !== '' ? `${invoiceForm.igst}%` : ''}
                                InputProps={{ readOnly: true }}
                                // helperText="From Supervisor Master"
                              />
                            </Grid>
                          )}
                          {section.paymentStatus === 'Paid' && (
                            <>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth size="small"
                                  label="Payment Received"
                                  type="number"
                                  value={invoiceForm.paymentReceived}
                                  onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'paymentReceived', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth size="small"
                                  label="TDS Amount"
                                  type="number"
                                  value={invoiceForm.tdsAmount}
                                  onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'tdsAmount', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth size="small"
                                  label="Total Amount"
                                  value={invoiceForm.totalAmount}
                                  InputProps={{ readOnly: true }}
                                  // helperText="Payment Received - TDS Amount"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth size="small"
                                  label="Bill TAT"
                                  type="number"
                                  value={invoiceForm.billTAT}
                                  onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'billTAT', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth size="small"
                                  label="Payment TAT"
                                  type="number"
                                  value={invoiceForm.paymentTAT}
                                  onChange={(e) => updateInvoiceFormField(group.supervisorId, section.bank, section.product, 'paymentTAT', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  fullWidth size="small"
                                  label="Total TAT"
                                  value={invoiceForm.totalTAT}
                                  InputProps={{ readOnly: true }}
                                  // helperText="Bill TAT + Payment TAT"
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => closeInvoiceForm(group.supervisorId, section.bank, section.product)}
                            disabled={isInvoiceSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleInvoiceSave(group.supervisorId, section.bank, section.product)}
                            disabled={isInvoiceSaving}
                            startIcon={isInvoiceSaving ? <CircularProgress size={16} /> : null}
                          >
                            {editingInvoiceId ? 'Update' : 'Save'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  )}
                </Box>
                );
              })}
            </Paper>
          );
        })
      )}
    </Box>
  );
};

export default BillingMaster;
